import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import FreightAdminTableToolbar from "../../../components/admin/FreightAdminTableToolbar";
import FreightProfileStatusDtActions from "../../../components/admin/FreightProfileStatusDtActions";
import { AdminDataTable } from "../../../components/datatable";
import { FREIGHT_PROFILE_STATUS_COLUMNS } from "../../../config/freightProfileStatusAdminColumns";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { useFreightAdminTablePrefs } from "../../../hooks/useFreightAdminTablePrefs";
import { changeFreightProfileStatusMasterStatus, getFreightProfileStatusIndex } from "../../../utils/api";
import { freightNamedListFromResponse } from "../../../utils/freightAdminListResponse";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const TABLE_ID = "freight_profile_status_v2";
const COLUMN_DEFS = FREIGHT_PROFILE_STATUS_COLUMNS;
const LIST_KEY = "freightProfileStatusList";

const sortRows = (list) =>
  [...list].sort((a, b) => {
    const o = Number(a.order_no) - Number(b.order_no);
    if (o !== 0) return o;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const FreightProfileStatusPage = () => {
  const navigate = useNavigate();
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Profile status" }
    ],
    [roleLabel]
  );

  const columnDefs = useMemo(() => COLUMN_DEFS, []);
  const {
    visibility,
    toggleColumn,
    resetColumnsVisible,
    resetColumnOrder,
    orderedColumnDefs,
    onColumnReorder
  } = useFreightAdminTablePrefs(TABLE_ID, columnDefs);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFreightProfileStatusIndex();
      setRows(sortRows(freightNamedListFromResponse(res, LIST_KEY)));
    } catch (e) {
      showError(e.message || "Failed to load profile statuses");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const getNavigate = useCallback(() => navigate, [navigate]);

  const handleToggleStatus = useCallback(
    async (row) => {
      if (!row?.id) return;
      const ok = window.confirm(
        String(row.status) === "1" ? "Deactivate this profile status?" : "Activate this profile status?"
      );
      if (!ok) return;
      try {
        const res = await changeFreightProfileStatusMasterStatus({ id: row.id });
        showSuccess(res?.message || "Status updated");
        await load();
      } catch (e) {
        showError(e.message || "Update failed");
      }
    },
    [load]
  );

  const renderActions = useCallback(
    (row) => (
      <FreightProfileStatusDtActions row={row} getNavigate={getNavigate} onToggleStatus={handleToggleStatus} />
    ),
    [getNavigate, handleToggleStatus]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [r.name, r.sub_status, r.text_color, r.background_color, r.order_no, r.status, r.id]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, search]);

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px]">
        <FreightAdminTableToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Filter by name, colors, order…"
          columnDefs={orderedColumnDefs}
          visibility={visibility}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumnsVisible}
          onResetColumnOrder={resetColumnOrder}
          rightSlot={
            <Link to="/freight/profile_status/new" className="btn-primary no-underline inline-block">
              + New status
            </Link>
          }
        />

        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
            <h3 className="m-0">Freight profile status ({filteredRows.length})</h3>
            <button type="button" className="btn-outline text-sm" onClick={() => load()}>
              Refresh
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Labels shown during freight onboarding / review. Set display colors to match your app theme.
          </p>

          <AdminDataTable
            columnDefs={orderedColumnDefs}
            visibility={visibility}
            rows={filteredRows}
            loading={loading}
            emptyText="No profile statuses yet."
            renderActions={renderActions}
            onColumnReorder={onColumnReorder}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightProfileStatusPage;
